<template>
  <div class="demo-progress">
    <!-- Progress bar -->
    <div class="demo-progress__bar">
      <div
        class="demo-progress__fill"
        :style="fillStyle"
        role="progressbar"
        :aria-valuenow="progressPercentage"
        :aria-valuemin="0"
        :aria-valuemax="100"
        :aria-label="`Demo progress: ${Math.round(progressPercentage)}%`"
      />
    </div>

    <!-- Step counter -->
    <div class="demo-progress__info">
      <span class="demo-progress__counter">
        Step {{ currentStepNumber }} of {{ totalSteps }}
      </span>
      <span class="demo-progress__percentage">
        {{ Math.round(progressPercentage) }}%
      </span>
    </div>

    <!-- Step indicators (dots) -->
    <div
      v-if="showStepIndicators"
      class="demo-progress__indicators"
    >
      <button
        v-for="(step, index) in stepIndicators"
        :key="index"
        class="demo-progress__dot"
        :class="getDotClass(index)"
        :title="`Go to step ${index + 1}`"
        :aria-label="`Step ${index + 1}${step.completed ? ' (completed)' : step.current ? ' (current)' : ''}`"
        :disabled="!allowStepNavigation"
        @click="selectStep(index)"
      />
    </div>
  </div>
</template>

<script>
import { PROGRESS, ANIMATION, BREAKPOINTS } from '@/demo/constants';

/**
 * DemoProgressBar Component
 * Visual progress indicator showing current step, completion status,
 * and step navigation dots
 * 
 * @component
 * @example
 * <DemoProgressBar 
 *   :currentStep="2"
 *   :totalSteps="13"
 *   :completedSteps="[0, 1]"
 *   @select-step="goToStep"
 * />
 */
export default {
  name: 'DemoProgressBar',

  props: {
    /**
     * Current step index (0-based)
     */
    currentStep: {
      type: Number,
      required: true,
      validator: (value) => value >= 0,
    },

    /**
     * Total number of steps
     */
    totalSteps: {
      type: Number,
      required: true,
      validator: (value) => value > 0,
    },

    /**
     * Array of completed step indices
     */
    completedSteps: {
      type: Array,
      default: () => [],
    },

    /**
     * Show step indicator dots
     */
    showStepIndicators: {
      type: Boolean,
      default: true,
    },

    /**
     * Allow clicking on step indicators to navigate
     * (only for completed/current steps)
     */
    allowStepNavigation: {
      type: Boolean,
      default: false,
    },

    /**
     * Show percentage text
     */
    showPercentage: {
      type: Boolean,
      default: true,
    },

    /**
     * Animate progress bar transitions
     */
    animated: {
      type: Boolean,
      default: true,
    },

    /**
     * Use linear or multi-step progress display
     */
    displayMode: {
      type: String,
      default: 'linear', // 'linear' or 'steps'
      validator: (value) => ['linear', 'steps'].includes(value),
    },
  },

  emits: {
    /**
     * Emitted when a step indicator is clicked
     * @param {number} stepIndex - The selected step index
     */
    'select-step': null,
  },

  data() {
    return {
      PROGRESS,
    };
  },

  computed: {
    /**
     * Current step number (1-based for display)
     */
    currentStepNumber() {
      return this.currentStep + 1;
    },

    /**
     * Calculate progress as percentage (0-100)
     */
    progressPercentage() {
      return (this.currentStepNumber / this.totalSteps) * 100;
    },

    /**
     * Calculate fill percentage based on display mode
     */
    fillPercentage() {
      if (this.displayMode === 'steps') {
        // For step mode, progress is based on completed steps
        return (this.completedSteps.length / this.totalSteps) * 100;
      }
      return this.progressPercentage;
    },

    /**
     * Fill bar style
     */
    fillStyle() {
      return {
        width: `${this.fillPercentage}%`,
        transition: this.animated ? `width ${ANIMATION.DURATION}ms ${ANIMATION.EASING}` : 'none',
      };
    },

    /**
     * Generate step indicator data
     */
    stepIndicators() {
      return Array.from({ length: this.totalSteps }, (_, index) => ({
        index,
        completed: this.completedSteps.includes(index),
        current: this.currentStep === index,
      }));
    },

    /**
     * Check if on mobile viewport
     */
    isMobile() {
      return typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.TABLET;
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    /**
     * Get CSS class for step indicator dot
     */
    getDotClass(index) {
      const classes = ['demo-progress__dot'];

      if (this.stepIndicators[index].current) {
        classes.push('demo-progress__dot--current');
      }

      if (this.stepIndicators[index].completed) {
        classes.push('demo-progress__dot--completed');
      }

      return classes;
    },

    /**
     * Handle step indicator click
     */
    selectStep(stepIndex) {
      if (this.allowStepNavigation && this.canSelectStep(stepIndex)) {
        this.$emit('select-step', stepIndex);
        this.announceSelection(stepIndex);
      }
    },

    /**
     * Check if step can be navigated to
     */
    canSelectStep(stepIndex) {
      // Allow current step, completed steps
      return (
        stepIndex === this.currentStep ||
        this.completedSteps.includes(stepIndex)
      );
    },

    /**
     * Announce step selection to screen readers
     */
    announceSelection(stepIndex) {
      if (this.$announce) {
        this.$announce(`Navigated to step ${stepIndex + 1}`);
      }
    },

    /**
     * Handle window resize
     */
    handleResize() {
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped lang="scss">
.demo-progress {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: #1e1e2e;
  border-radius: 4px;

  @media (max-width: 480px) {
    padding: 8px;
    gap: 8px;
  }
}

.demo-progress__bar {
  width: 100%;
  height: 4px;
  background-color: #2a2a3e;
  border-radius: 2px;
  overflow: hidden;
  display: flex;
}

.demo-progress__fill {
  height: 100%;
  background: linear-gradient(90deg, #007aff, #0051d5);
  border-radius: 2px;
  background-size: 200% 100%;
  animation: demo-progress-shine 2s ease-in-out infinite;
}

@keyframes demo-progress-shine {
  0% {
    background-position: 200% 0;
  }
  50% {
    background-position: 0% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.demo-progress__info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
  font-size: 12px;
  color: #888888;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 11px;
  }
}

.demo-progress__counter {
  font-weight: 500;
  color: #a0a0b0;
}

.demo-progress__percentage {
  font-weight: 600;
  color: #007aff;
  min-width: 35px;
  text-align: right;
}

.demo-progress__indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 4px 0;

  @media (max-width: 480px) {
    gap: 4px;
  }
}

.demo-progress__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #3a3a52;
  background-color: #2a2a3e;
  cursor: pointer;
  transition: all 200ms ease-out;
  padding: 0;

  &:hover:not(:disabled) {
    border-color: #007aff;
    box-shadow: 0 0 8px rgba(0, 122, 255, 0.3);
  }

  &:focus-visible {
    outline: 2px solid #007aff;
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  // Current step indicator
  &--current {
    width: 12px;
    height: 12px;
    background-color: #007aff;
    border-color: #007aff;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
    animation: demo-pulse-current 2s ease-in-out infinite;

    @keyframes demo-pulse-current {
      0% {
        box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.3);
      }
      100% {
        box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
      }
    }
  }

  // Completed step indicator
  &--completed {
    background-color: #22c55e;
    border-color: #22c55e;

    &::after {
      content: '✓';
      position: absolute;
      width: 8px;
      height: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 6px;
      font-weight: bold;
      color: #ffffff;
      left: -1px;
      top: -1px;
    }
  }
}

// Alternative: Responsive indicators (hide on very small screens)
@media (max-width: 320px) {
  .demo-progress__indicators {
    display: none;
  }
}
</style>
